import { AlertCircle, CheckCircle, Loader2, XCircle } from "lucide-react";
import { useState } from "react";

/* eslint-disable max-lines-per-function */

export interface BulkRejectionResult {
  rejected: number;
  failed: Array<{ id: string; title: string; error: string }>;
}

interface BulkRejectionDialogProps {
  readonly isOpen: boolean;
  readonly submissionIds: string[];
  readonly submissions: Array<{ id: string; title: string; posterUrl?: string | null }>;
  readonly onConfirm: (reason: string, notes: string) => Promise<BulkRejectionResult>;
  readonly onCancel: () => void;
  readonly onComplete: () => void;
}

const REJECTION_REASONS = [
  { value: "", label: "Select a reason..." },
  { value: "incomplete_metadata", label: "Incomplete Metadata" },
  { value: "quality_issues", label: "Quality Issues" },
  { value: "rights_licensing", label: "Rights/Licensing Issues" },
  { value: "content_violations", label: "Content Violations" },
  { value: "other", label: "Other" },
] as const;

type DialogState = "confirm" | "processing" | "results";

/**
 * Get the dialog title based on current state
 */
function getDialogTitle(state: DialogState): string {
  switch (state) {
    case "processing":
      return "Rejecting Submissions...";
    case "results":
      return "Bulk Rejection Complete";
    default:
      return "Bulk Reject Submissions";
  }
}

/**
 * BulkRejectionDialog - Modal for rejecting multiple submissions at once
 *
 * Features:
 * - Preview of selected submissions (first 5 with "and X more" indicator)
 * - Categorized rejection reason (required)
 * - Shared notes field applied to all rejections
 * - Progress indicator during bulk operation
 * - Results summary showing successes and failures
 */
export function BulkRejectionDialog({
  isOpen,
  submissionIds,
  submissions,
  onConfirm,
  onCancel,
  onComplete,
}: BulkRejectionDialogProps) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [dialogState, setDialogState] = useState<DialogState>("confirm");
  const [results, setResults] = useState<BulkRejectionResult | null>(null);

  const selectedSubmissions = submissions.filter((s) => submissionIds.includes(s.id));
  const previewSubmissions = selectedSubmissions.slice(0, 5);
  const remainingCount = selectedSubmissions.length - 5;

  const handleConfirm = async () => {
    if (!reason) return;
    setDialogState("processing");
    try {
      const result = await onConfirm(reason, notes);
      setResults(result);
      setDialogState("results");
    } catch {
      setResults({
        rejected: 0,
        failed: selectedSubmissions.map((s) => ({ id: s.id, title: s.title, error: "Network error" })),
      });
      setDialogState("results");
    }
  };

  const handleClose = () => {
    setReason("");
    setNotes("");
    setDialogState("confirm");
    setResults(null);
    if (dialogState === "results") {
      onComplete();
    } else {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0">
            {dialogState === "processing" ? (
              <Loader2 className="w-6 h-6 text-red-400 animate-spin" />
            ) : (
              <XCircle className="w-6 h-6 text-red-400" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{getDialogTitle(dialogState)}</h3>
            <p className="text-sm text-neutral-400">
              {dialogState === "results" && results
                ? `${results.rejected} rejected, ${results.failed.length} failed`
                : `${submissionIds.length} submissions selected`}
            </p>
          </div>
        </div>

        {/* Confirmation View */}
        {dialogState === "confirm" && (
          <>
            {/* Preview List */}
            <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-neutral-400 mb-2">Selected submissions:</p>
              <ul className="space-y-2">
                {previewSubmissions.map((submission) => (
                  <li key={submission.id} className="flex items-center gap-2 text-sm text-white">
                    {submission.posterUrl ? (
                      <img src={submission.posterUrl} alt="" className="w-8 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-8 h-12 bg-neutral-700 rounded flex items-center justify-center">
                        <span className="text-xs text-neutral-500">?</span>
                      </div>
                    )}
                    <span className="truncate">{submission.title}</span>
                  </li>
                ))}
              </ul>
              {remainingCount > 0 && <p className="text-sm text-neutral-500 mt-2">...and {remainingCount} more</p>}
            </div>

            {/* Warning */}
            <div className="bg-red-600/10 border border-red-600/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-400">
                All selected providers will be notified that their submissions have been rejected with the same reason.
              </p>
            </div>

            {/* Rejection Reason (Required) */}
            <div className="mb-4">
              <label htmlFor="bulk-rejection-reason" className="text-sm text-neutral-400 block mb-2">
                Rejection Reason <span className="text-red-400">*</span>
              </label>
              <select
                id="bulk-rejection-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {REJECTION_REASONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Shared Notes */}
            <div className="mb-6">
              <label htmlFor="bulk-rejection-notes" className="text-sm text-neutral-400 block mb-2">
                Shared Additional Notes (optional)
              </label>
              <textarea
                id="bulk-rejection-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes that will be included in all rejection notifications..."
                rows={3}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none text-sm"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleConfirm();
                }}
                disabled={!reason}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject {submissionIds.length} Submissions
              </button>
            </div>
          </>
        )}

        {/* Processing View */}
        {dialogState === "processing" && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 text-red-400 animate-spin mx-auto mb-4" />
            <p className="text-neutral-400">Processing {submissionIds.length} submissions...</p>
            <p className="text-sm text-neutral-500 mt-2">This may take a moment</p>
          </div>
        )}

        {/* Results View */}
        {dialogState === "results" && results && (
          <>
            {/* Success Summary */}
            {results.rejected > 0 && (
              <div className="bg-amber-600/10 border border-amber-600/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-amber-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {results.rejected} submission{results.rejected === 1 ? "" : "s"} rejected successfully
                  </span>
                </div>
              </div>
            )}

            {/* Failures */}
            {results.failed.length > 0 && (
              <div className="bg-red-600/10 border border-red-600/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {results.failed.length} submission{results.failed.length === 1 ? "" : "s"} failed
                  </span>
                </div>
                <ul className="space-y-1 text-sm text-neutral-400 max-h-32 overflow-y-auto">
                  {results.failed.map((failure) => (
                    <li key={failure.id} className="flex items-start gap-2">
                      <span className="text-neutral-500">•</span>
                      <span>
                        <span className="text-white">{failure.title}</span>: {failure.error}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              className="w-full px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}
